import { apiSlice } from '../../../store/api/apiSlice';

// Types
export interface CronRunDTO {
  id: string;
  started_at: string;
  finished_at?: string;
  status: string;
  trigger_type: string;
  triggered_by?: string;
  duration_ms?: number;
  error_message?: string;
}

export interface CronListItem {
  name: string;
  description: string;
  cron: string;
  topic: string;
  next_run?: string;
  last_run?: CronRunDTO;
  active_run?: CronRunDTO;
}

export interface ListCronsResponse {
  crons: CronListItem[];
}

export interface ListCronRunsResponse {
  runs: CronRunDTO[];
}

export interface TriggerCronResponse {
  ok: boolean;
  cron_name: string;
  topic: string;
  run?: CronRunDTO;
}

export interface CronTriggerErrorResponse {
  error?: string;
  error_description?: string;
  active_run?: CronRunDTO;
}

export interface TriggerCronRequest {
  name: string;
  event_args?: Record<string, unknown>;
}

// API endpoints
export const cronApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // List all cron jobs with details
    listCrons: builder.query<ListCronsResponse, void>({
      query: () => '/admin/cron',
      providesTags: ['CronJobs'],
    }),

    // List runs for a specific cron job
    listCronRuns: builder.query<ListCronRunsResponse, { name: string; limit?: number }>({
      query: ({ name, limit }) => ({
        url: `/admin/cron/${encodeURIComponent(name)}/runs`,
        params: limit ? { limit } : undefined,
      }),
      providesTags: (_result, _error, { name }) => [{ type: 'CronRuns', id: name }],
    }),

    // Trigger a cron job manually
    triggerCron: builder.mutation<TriggerCronResponse, TriggerCronRequest>({
      query: ({ name, event_args }) => ({
        url: `/admin/cron/${encodeURIComponent(name)}/trigger`,
        method: 'POST',
        body: event_args ? { event_args } : undefined,
      }),
      invalidatesTags: (_result, _error, { name }) => ['CronJobs', { type: 'CronRuns', id: name }],
    }),
  }),
});

export const {
  useListCronsQuery,
  useListCronRunsQuery,
  useLazyListCronRunsQuery,
  useTriggerCronMutation,
} = cronApi;

export function readCronTriggerError(error: unknown): CronTriggerErrorResponse {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return {};
  }

  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== 'object') {
    return {};
  }

  const payload = data as Record<string, unknown>;
  return {
    error: typeof payload.error === 'string' ? payload.error : undefined,
    error_description:
      typeof payload.error_description === 'string' ? payload.error_description : undefined,
    active_run: payload.active_run as CronRunDTO | undefined,
  };
}
